import {
    AccordionDetails,
    AccordionSummary,
    Box,
    Accordion as MuiAccordion,
    Typography,
} from "@mui/material";
import { Button } from "components/Button/Button";
import { Icon, IconLike } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import { PropsWithChildren, ReactNode } from "react";
import "./Accordion.scss";

export type AccordionProps = PropsWithChildren<{
    title: ReactNode;
    className?: string;
    onDelete?: () => any;
    isDisabled?: boolean;
    isExpanded?: boolean;
    isExpandedByDefault?: boolean;
    onExpandedChange?: (value: boolean) => void;
    isBorderless?: boolean;
    expandOnIconClick?: boolean;
    deleteIcon?: IconLike;
    actionButton?: ReactNode;
    hasExpandIcon?: boolean;
    stopTitleClickPropagation?: boolean;
    hasBottomExpandIcon?: boolean;
    disableDetailsPadding?: boolean;
}>;

export const Accordion = ({
    title,
    children,
    isExpandedByDefault,
    className,
    onDelete,
    isExpanded,
    onExpandedChange,
    isDisabled,
    isBorderless,
    expandOnIconClick,
    deleteIcon,
    actionButton,
    hasExpandIcon = true,
    hasBottomExpandIcon,
    stopTitleClickPropagation,
    disableDetailsPadding,
}: AccordionProps) => {
    const expandedProps =
        isExpanded !== undefined && onExpandedChange
            ? {
                  expanded: isExpanded,
                  onChange: (_, expanded) =>
                      expandOnIconClick ? null : onExpandedChange?.(expanded),
              }
            : {};

    return (
        <MuiAccordion
            className={`accordion ${className ?? ""} ${isBorderless ? "borderless" : ""} ${
                expandOnIconClick ? "expandOnIconClick" : ""
            }`}
            defaultExpanded={isExpandedByDefault}
            disableGutters
            {...expandedProps}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <AccordionSummary
                    expandIcon={
                        hasExpandIcon ? (
                            <div
                                onClick={() =>
                                    expandOnIconClick ? onExpandedChange?.(!isExpanded) : null
                                }
                            >
                                <Icon icon={IconCollection.chevronDown} className="expandIcon" />
                            </div>
                        ) : null
                    }
                    style={{ flexGrow: 1 }}
                >
                    <Typography
                        component="div"
                        onClick={(e) => stopTitleClickPropagation && e.stopPropagation()}
                    >
                        {title}
                    </Typography>
                </AccordionSummary>
                {actionButton}
                {onDelete && (
                    <Box>
                        <Button
                            onClick={onDelete}
                            disabled={isDisabled}
                            icon={deleteIcon ?? IconCollection.close}
                            variation="clear"
                        />
                    </Box>
                )}
            </Box>
            {children != null ? (
                <AccordionDetails sx={disableDetailsPadding ? { padding: 0 } : undefined}>
                    {children}
                </AccordionDetails>
            ) : (
                <></>
            )}
            {hasBottomExpandIcon && onExpandedChange && (
                <Box>
                    <IconButton
                        icon={isExpanded ? IconCollection.chevronUp : IconCollection.chevronDown}
                        className="bottomExpandIcon"
                        onClick={() => onExpandedChange?.(!isExpanded)}
                    />
                </Box>
            )}
        </MuiAccordion>
    );
};
